import axios from 'axios';
import { API_URL } from '../config/api';

let accessToken = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
let refreshPromise = null;
const tokenListeners = new Set();
let onAuthLost = () => {};

export function getAccessToken() {
  return accessToken;
}

export function setAccessToken(token) {
  accessToken = token || null;
  if (typeof localStorage !== 'undefined') {
    if (accessToken) {
      localStorage.setItem('token', accessToken);
    } else {
      localStorage.removeItem('token');
    }
  }
  tokenListeners.forEach((fn) => fn(accessToken));
}

export function subscribeAccessToken(fn) {
  tokenListeners.add(fn);
  return () => tokenListeners.delete(fn);
}

export function setOnAuthLost(fn) {
  onAuthLost = typeof fn === 'function' ? fn : () => {};
}

export function accessTokenExpiresAt(token) {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(padded));
    return json.exp ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isAuthEndpoint(config) {
  const url = `${config.baseURL || ''}${config.url || ''}`;
  return /\/auth\/(login|refresh|logout)(\?|$)/.test(url);
}

export const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = accessToken;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!original || error.response?.status !== 401 || original._retry || isAuthEndpoint(original)) {
      return Promise.reject(error);
    }
    original._retry = true;
    try {
      const token = await refreshAccessToken();
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${token}`;
      return apiClient(original);
    } catch (refreshError) {
      const status = refreshError.response?.status;
      if (status === 401 || status === 403) {
        setAccessToken(null);
        onAuthLost();
      }
      return Promise.reject(error);
    }
  }
);

export async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = axios
    .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true, timeout: 15000 })
    .then((resp) => {
      const token = resp.data.access_token;
      setAccessToken(token);
      if (resp.data.user && typeof localStorage !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(resp.data.user));
      }
      return token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}
